<script lang="ts">
  import { querystring, location, replace } from "svelte-spa-router";
  import TextNew from "../components/TextNew.svelte";
  import ButtonGroup from "../components/spend-report/ButtonGroup.svelte";
  import DisplayCard from "../components/spend-report/DisplayCard.svelte";
  import OverviewCardContent from "../components/spend-report/OverviewCardContent.svelte";
  import GPCalculator from "../components/spend-report/GPCalculator.svelte";
  import RadioButton from "../components/RadioButton.svelte";
  import BreakdownChart from "../components/spend-report/BreakdownChart.svelte";
  import { createQuery } from "@tanstack/svelte-query";
  import { store, filteringDate } from "../helpers/store";
  import { derived, get, writable } from "svelte/store";
  import {
    differenceInDays,
    format,
    startOfWeek,
    subDays,
    subMonths,
    subQuarters,
    subWeeks,
  } from "date-fns";
  import { DateFilterType, type SpendBreakdownFilterType } from "../helpers/types";
  import { createTopPriceChangesQuery, spendReportKeys } from "../queries/spend-report";
  import { getSpendReport, type GetSpendReportOutput } from "../api/spend-report";
  import { calculatePercentageChange } from "../helpers/math";
  import type { SpendParams } from "../actions/tooltip";
  import UploadInvoiceEmptyState from "../components/empty-states/UploadInvoiceEmptyState.svelte";
  import { createGetSubscriptionQuery } from "../queries/subscriptions";
  import { hasNoSubscription, openSubscriptionModal } from "../helpers/subscription";
  import { onMount } from "svelte";
  import { track } from "../helpers/mixpanel";
  import ButtonNew from "../components/buttons/ButtonNew.svelte";
  import TopPriceChangesCard from "../components/spend-report/TopPriceChangesCard.svelte";
  import PageHeader from "../components/headers/PageHeaderNew.svelte";
  import { globalDatePickerStore } from "../stores/date-picker";
  import DatePickerPanel from "../components/date-picker/DatePickerPanel.svelte";
  import EmptyStateBasic from "../components/empty-states/EmptyStateBasic.svelte";
  import ListIcon from "../components/icons/ListIcon.svelte";
  import { filteredChatSpendBreakdowns } from "../derived-data/spend-report";
  import { subscriptionModal } from "../stores/subscription-modal-store";
  import { createModal } from "../components/modals/createModal";
  import InvoiceUploadModal from "../components/invoices/InvoiceUploadModal.svelte";
  import {
    type GpQueryParams,
    createGPQuery,
    createUnfilteredGPQueryParams,
  } from "../queries/transactions";
  import { createRecordGPTargetMutation } from "../mutations/transactions";
  import IconedHeaderLink from "../components/headers/IconedHeaderLink.svelte";
  import Icon from "../components/icons/Icon.svelte";
  import SalesImportModal from "../components/invoices/SalesImportModal.svelte";
  import { createDropdownMenu, type MenuParams } from "../components/dropdown-menu/create";
  import {
    createBuyerFilterMenuItems,
    createChatFilterMenuItems,
    syncQueryParamsWithChatFilters,
  } from "../components/dropdown-menu/chat-filter-menu";
  import { createPersistedLocalStore } from "../stores/persisted-local-store";
  import {
    GP_CHAT_FILTER_STORAGE_KEY,
    GP_CHAT_FILTER_VERSION,
    CHAT_FILTER_PARAM_KEY,
  } from "../components/invoices/constants";
  import { restorePersistedChatFilters } from "../helpers/gp";
  import { t } from "../helpers/translations";
  import { currencyFormatter, dateLocale } from "../helpers/localise";
  import { createSuppliersChatsByBuyerIDsQuery } from "../queries/suppliers";
  import difference from "lodash/difference";
  import FilterMenu, {
    type FilterMenuItemDetails,
  } from "../components/dropdown-menu/FilterMenu.svelte";
  import BreakdownFilterMenu from "../components/spend-report/BreakdownFilterMenu.svelte";
  import { isEmpty } from "lodash";
  import { selectedBuyer } from "../stores/buyer";
  import { createGetLandingQuery } from "../helpers/landing";

  const DEFAULT_TOTALS = {
    total_net: 0,
    total_gross: 0,
    total_vat: 0,
    net_credit_note: 0,
    credit_note_vat: 0,
    net_invoice: 0,
    invoice_vat: 0,
  };

  const GP_CAPTION_DATE_FORMAT = "dd MMM";

  let breakdownFilterType: SpendBreakdownFilterType = "net";

  const {
    contentAttrs,
    underlayAttrs,
    open: isUploadInvoiceOpen,
    triggerElement,
    closeButtonAttrs,
    ...modal
  } = createModal({
    onModalClose: clearModalParams,
  });

  const {
    open: chatFilterOpen,
    menu: chatFilterMenu,
    menuAttrs: chatFilterMenuAttrs,
    triggerAttrs: chatFilterTriggerAttrs,
    onTriggerClick: chatFilterOnTriggerClick,
    triggerElement: chatFilterTriggerElement,
  } = createDropdownMenu();

  const {
    open: breakdownFilterOpen,
    menu: breakdownFilterMenu,
    menuAttrs: breakdownFilterMenuAttrs,
    triggerAttrs: breakdownFilterTriggerAttrs,
    onTriggerClick: breakdownFilterOnTriggerClick,
    triggerElement: breakdownFilterTriggerElement,
  } = createDropdownMenu();

  const {
    open: buyerFilterOpen,
    menu: buyerFilterMenu,
    menuAttrs: buyerFilterMenuAttrs,
    triggerAttrs: buyerFilterTriggerAttrs,
    onTriggerClick: buyerFilterOnTriggerClick,
    triggerElement: buyerFilterTriggerElement,
  } = createDropdownMenu();

  const {
    open: aggregateChatFilterOpen,
    menu: aggregateChatFilterMenu,
    menuAttrs: aggregateChatFilterMenuAttrs,
    triggerAttrs: aggregateChatFilterTriggerAttrs,
    onTriggerClick: aggregateChatFilterOnTriggerClick,
    triggerElement: aggregateChatFilterTriggerElement,
  } = createDropdownMenu();

  const filterMenuOptions: MenuParams = {
    strategy: "absolute",
    placement: "bottom-end",
    closeOnClickOutside: true,
    flipOnOverflow: true,
    offset: 6,
  };

  const breakdownFilterMenuOptions: MenuParams = {
    ...filterMenuOptions,
    placement: "bottom-start",
  };

  const {
    underlayAttrs: salesImportModalUnderlayAttrs,
    contentAttrs: salesImportModalContentAttrs,
    open,
    triggerElement: salesImportModalTriggerElement,
    ...salesImportModal
  } = createModal();

  const chatFiltersByID = createPersistedLocalStore<Record<string, boolean>>(
    GP_CHAT_FILTER_STORAGE_KEY,
    {},
    GP_CHAT_FILTER_VERSION
  );

  let hasRestoredFilters = false;

  $: if (hasRestoredFilters && $location === "/spend-report") {
    syncQueryParamsWithChatFilters($querystring, chatFiltersByID);
  }

  const { dateRangeFilterType } = globalDatePickerStore;
  let breakdownChatFilters: Record<string, boolean> = {};

  $: buyerID = $selectedBuyer?.id ?? "";

  const previousPeriod = derived(
    [globalDatePickerStore, dateRangeFilterType],
    ([$datePickerState, $filterType]) => {
      const { confirmedStartDate, confirmedEndDate } = $datePickerState;
      switch ($filterType) {
        case DateFilterType.Past7Days: {
          return {
            startDate: subDays(confirmedStartDate, 7),
            endDate: subDays(confirmedEndDate, 7),
          };
        }
        case DateFilterType.Past14Days: {
          return {
            startDate: subDays(confirmedStartDate, 14),
            endDate: subDays(confirmedEndDate, 14),
          };
        }
        case DateFilterType.Week: {
          const previousEndDate = subWeeks(confirmedEndDate, 1);
          const previousStartDate = startOfWeek(previousEndDate, { weekStartsOn: 1 });
          return { startDate: previousStartDate, endDate: subWeeks(confirmedEndDate, 1) };
        }
        case DateFilterType.Past30Days: {
          return {
            startDate: subDays(confirmedStartDate, 30),
            endDate: subDays(confirmedEndDate, 30),
          };
        }
        case DateFilterType.Month:
        case DateFilterType.ThisMonth: {
          return {
            startDate: subMonths(confirmedStartDate, 1),
            endDate: subMonths(confirmedEndDate, 1),
          };
        }
        case DateFilterType.Quarter: {
          return {
            startDate: subQuarters(confirmedStartDate, 1),
            endDate: subQuarters(confirmedEndDate, 1),
          };
        }
        case DateFilterType.Custom: {
          const diff = differenceInDays(confirmedEndDate, confirmedStartDate);
          return {
            startDate: subDays(confirmedStartDate, diff),
            endDate: subDays(confirmedEndDate, diff),
          };
        }
        default:
          return { startDate: confirmedStartDate, endDate: confirmedEndDate };
      }
    }
  );

  $: currentPeriodQuery = createQuery({
    queryKey: spendReportKeys.result(buyerID, {
      start_date: $globalDatePickerStore.confirmedStartDate.toISOString(),
      end_date: $globalDatePickerStore.confirmedEndDate.toISOString(),
      filtering_date: $filteringDate,
      chats: [],
    }),
    queryFn: async ({
      queryKey: [_, __, buyer_id, { start_date, end_date, filtering_date, chats }],
    }) => getSpendReport({ buyer_id, start_date, end_date, filtering_date, chats }),
    enabled: !!buyerID,
  });

  $: previousPeriodQuery = createQuery({
    queryKey: spendReportKeys.result(buyerID, {
      start_date: $previousPeriod.startDate.toISOString(),
      end_date: $previousPeriod.endDate.toISOString(),
      filtering_date: $filteringDate,
      chats: [],
    }),
    queryFn: async ({
      queryKey: [_, __, buyer_id, { start_date, end_date, filtering_date, chats }],
    }) => getSpendReport({ buyer_id, start_date, end_date, filtering_date, chats }),
    enabled: !!buyerID,
  });

  function handleNetClick() {
    breakdownFilterType = "net";
  }

  function handleGrossClick() {
    breakdownFilterType = "gross";
  }

  $: breakdowns = sortBreakdownTotals(
    filteredChatSpendBreakdowns($currentPeriodQuery, breakdownChatFilters),
    breakdownFilterType
  );
  $: previousPeriodBreakdowns = sortBreakdownTotals(
    filteredChatSpendBreakdowns($previousPeriodQuery, breakdownChatFilters),
    breakdownFilterType
  );

  const unfilteredGpQueryParams = createUnfilteredGPQueryParams();
  const unfilteredGpQuery = createGPQuery(unfilteredGpQueryParams);

  const gpQueryParams = derived(
    [unfilteredGpQueryParams, chatFiltersByID],
    ([$params, $chatFiltersByID]) => {
      const chatFilterIDs = Object.entries($chatFiltersByID)
        .filter(([_chatID, selected]) => selected)
        .map(([chatID]) => chatID);

      return {
        ...$params,
        selectedChats: chatFilterIDs,
      } satisfies GpQueryParams;
    }
  );

  const gpQuery = createGPQuery(gpQueryParams);

  const landingQuery = createGetLandingQuery();

  const initialBuyerFiltersByID = derived(
    [selectedBuyer, landingQuery],
    ([$selectedBuyer, $landingQuery]) => {
      const buyerId = $selectedBuyer?.id ?? "";
      const buyers = ($landingQuery.data?.buyers ?? [])
        .filter((b) => b.subscription_status === "trialing" || b.subscription_status === "active")
        .map((b) => b.id);
      let gpBuyers = new Set($landingQuery.data?.gp_buyers[buyerId] ?? []);
      return buyers.reduce<{ [buyerId: string]: boolean }>((acc, buyer) => {
        acc[buyer] = gpBuyers.has(buyer);
        return acc;
      }, {});
    }
  );

  const buyerFiltersByID = writable($initialBuyerFiltersByID);

  initialBuyerFiltersByID.subscribe((filters) => {
    buyerFiltersByID.set(filters);
  });

  const onToggleBuyer = (buyer: FilterMenuItemDetails) => {
    buyerFiltersByID.update((filters) => {
      filters[buyer.id] = !filters[buyer.id];
      return filters;
    });
  };

  const onToggleAllBuyers = () => {
    buyerFiltersByID.update((filters) => {
      return Object.fromEntries(Object.keys(filters).map((key) => [key, true]));
    });
  };

  const initialAggregateChatFiltersByID = derived(
    [selectedBuyer, landingQuery],
    ([$selectedBuyer, $landingQuery]) => {
      const buyerId = $selectedBuyer?.id ?? "";
      const gpChats = Object.fromEntries(
        ($landingQuery.data?.gp_chats[buyerId] ?? []).map((c) => [c, true])
      );
      return gpChats;
    }
  );

  const aggregateChatFiltersByID = writable($initialAggregateChatFiltersByID);

  initialAggregateChatFiltersByID.subscribe((filters) => {
    aggregateChatFiltersByID.set(filters);
  });

  const onToggleAggregateChat = (chat: FilterMenuItemDetails) => {
    aggregateChatFiltersByID.update((filters) => {
      filters[chat.id] = !filters[chat.id];
      return filters;
    });
  };

  const onToggleAllAggregateChats = () => {
    aggregateChatFiltersByID.update(() => {
      return Object.fromEntries(
        Object.values($aggregateGpQuery.data?.chats || {}).map((c) => [c.chat_id, true])
      );
    });
  };

  const aggregateGpQueryParams = derived(
    [unfilteredGpQueryParams, aggregateChatFiltersByID, buyerFiltersByID],
    ([$params, $aggregateChatFiltersByID, $buyerFiltersByID]) => {
      const buyers = Object.entries($buyerFiltersByID)
        .filter(([_buyerID, selected]) => selected)
        .map(([buyerID]) => buyerID);

      const chatFilterIDs = Object.entries($aggregateChatFiltersByID)
        .filter(([_chatID, selected]) => selected)
        .map(([chatID]) => chatID);

      return {
        ...$params,
        buyerIds: buyers,
        selectedChats: chatFilterIDs,
        persist: true,
        showAllSuppliers: true,
      } satisfies GpQueryParams;
    }
  );

  const aggregateGpQuery = createGPQuery(aggregateGpQueryParams);

  const supplierChatsQueryParams = derived(
    [selectedBuyer, chatFiltersByID, unfilteredGpQuery],
    ([$selectedBuyer, $chatFiltersByID, $unfilteredGpQuery]) => {
      return {
        buyerIDs: [$selectedBuyer?.id ?? ""],
        chatIDs: difference(
          Object.keys($chatFiltersByID),
          $unfilteredGpQuery.data?.chats?.map((c) => c.chat_id) ?? []
        ),
      };
    }
  );
  const supplierChatsUnavailableForDateRange =
    createSuppliersChatsByBuyerIDsQuery(supplierChatsQueryParams);

  const chatFilterCount = derived(chatFiltersByID, (s) => Object.values(s).filter(Boolean).length);
  $: breakdownFilterCount = isEmpty(breakdownChatFilters)
    ? null
    : Object.entries(breakdownChatFilters).filter(([, selected]) => selected).length;

  const buyerFilterCount = derived(
    buyerFiltersByID,
    (s) => Object.values(s).filter(Boolean).length
  );

  const aggregateChatFilterCount = derived(
    aggregateChatFiltersByID,
    (s) => Object.values(s).filter(Boolean).length
  );

  const recordGPTargetMutation = createRecordGPTargetMutation();

  $: onGpTargetChange = (gpTarget: number) => {
    $recordGPTargetMutation.mutate(
      {
        buyer_id: buyerID,
        target: gpTarget,
      },

      {
        onSuccess: () => {
          $gpQuery.refetch();
        },
      }
    );
  };

  $: chatFilterMenuItems = createChatFilterMenuItems(
    $unfilteredGpQuery.data?.chats,
    $chatFiltersByID,
    $supplierChatsUnavailableForDateRange.data
  );

  $: buyerFilterMenuItems = createBuyerFilterMenuItems(
    ($landingQuery.data?.buyers || []).filter(
      (b) => b.subscription_status === "active" || b.subscription_status === "trialing"
    ),
    $buyerFiltersByID
  );

  $: aggregateChatFilterMenuItems = createChatFilterMenuItems(
    $aggregateGpQuery.data?.chats,
    $aggregateChatFiltersByID,
    $supplierChatsUnavailableForDateRange.data
  );

  $: totalRevenue = hasAllChatsFiltersToggledOff(chatFilterMenuItems)
    ? 0
    : $gpQuery.data?.revenue_status === "missing"
    ? null
    : $gpQuery.data?.total_revenue ?? 0;
  $: totalSpend = hasAllChatsFiltersToggledOff(chatFilterMenuItems)
    ? 0
    : $gpQuery.data?.total_spend || 0;
  $: grossProfit = hasAllChatsFiltersToggledOff(chatFilterMenuItems)
    ? 0
    : $gpQuery.data?.revenue_status === "missing"
    ? null
    : $gpQuery.data?.profit ?? 0;
  $: gpTarget = $gpQuery.data?.target_gp || null;
  $: isDefaultGp = $gpQuery.data?.is_default_gp;
  $: isProfitable = $gpQuery.data?.is_profitable;

  $: aggregateTotalRevenue = hasAllChatsFiltersToggledOff(chatFilterMenuItems)
    ? 0
    : $aggregateGpQuery.data?.revenue_status === "missing"
    ? null
    : $aggregateGpQuery.data?.total_revenue ?? 0;
  $: aggregateTotalSpend = hasAllChatsFiltersToggledOff(chatFilterMenuItems)
    ? 0
    : $aggregateGpQuery.data?.total_spend || 0;
  $: aggregateGrossProfit = hasAllChatsFiltersToggledOff(chatFilterMenuItems)
    ? 0
    : $aggregateGpQuery.data?.revenue_status === "missing"
    ? null
    : $aggregateGpQuery.data?.profit ?? 0;
  $: aggregateGpTarget = $aggregateGpQuery.data?.target_gp || null;
  $: aggregateIsDefaultGp = $aggregateGpQuery.data?.is_default_gp;
  $: aggregateIsProfitable = $aggregateGpQuery.data?.is_profitable;

  $: isAggregateGPLoading = $aggregateGpQuery.isLoading || $aggregateGpQuery.isPlaceholderData;

  $: showAggregateGpWidget = ($landingQuery.data?.buyers?.length ?? 0) > 1 && false;

  // Here we filter by supplier all totals we will use
  $: currentPeriodFilteredValues =
    breakdowns?.reduce((acc, b) => {
      return {
        total_net: acc.total_net + b.total_net,
        total_gross: acc.total_gross + b.total_gross,
        total_vat: acc.total_vat + b.total_vat,
        net_credit_note: acc.net_credit_note + b.net_credit_note,
        credit_note_vat: acc.credit_note_vat + b.credit_note_vat,
        net_invoice: acc.net_invoice + b.net_invoice,
        invoice_vat: acc.invoice_vat + b.invoice_vat,
      };
    }, DEFAULT_TOTALS) ?? DEFAULT_TOTALS;

  $: previousPeriodFilteredValues =
    previousPeriodBreakdowns?.reduce((acc, b) => {
      return {
        total_net: acc.total_net + b.total_net,
        total_gross: acc.total_gross + b.total_gross,
        total_vat: acc.total_vat + b.total_vat,
        net_credit_note: acc.net_credit_note + b.net_credit_note,
        credit_note_vat: acc.credit_note_vat + b.credit_note_vat,
        net_invoice: acc.net_invoice + b.net_invoice,
        invoice_vat: acc.invoice_vat + b.invoice_vat,
      };
    }, DEFAULT_TOTALS) ?? DEFAULT_TOTALS;

  $: topPriceChangesQuery = createTopPriceChangesQuery(
    buyerID,
    $globalDatePickerStore.confirmedStartDate.toISOString(),
    $globalDatePickerStore.confirmedEndDate.toISOString(),
    $filteringDate
  );

  $: filteredPriceChanges = isEmpty(breakdownChatFilters)
    ? $topPriceChangesQuery.data?.price_changes
    : $topPriceChangesQuery.data?.price_changes.filter((p) => {
        return breakdownChatFilters[p.chat_id];
      });

  $: previousPeriodNet =
    breakdownFilterCount && breakdownFilterCount !== breakdowns?.length
      ? previousPeriodFilteredValues.total_net
      : $previousPeriodQuery.data?.total_net || 0;
  $: totalNetPercentChange = calculatePercentageChange(
    currentPeriodFilteredValues.total_net,
    previousPeriodNet
  );

  // If supplier filter is unchanged from default, we use total value from previous period
  // regardless of the suppliers included in that
  $: previousGross =
    breakdownFilterCount && breakdownFilterCount !== breakdowns?.length
      ? previousPeriodFilteredValues.total_gross
      : $previousPeriodQuery.data?.total_spend || 0;
  $: totalGrossPercentChange = calculatePercentageChange(
    currentPeriodFilteredValues.total_gross,
    previousGross
  );

  $: totalVAT = currentPeriodFilteredValues.total_vat - currentPeriodFilteredValues.credit_note_vat;
  $: formattedVAT = $currencyFormatter.format(totalVAT / 100);
  $: filteredGrossSpend = $currencyFormatter.format(currentPeriodFilteredValues.total_gross / 100);
  $: filteredNetSpend = $currencyFormatter.format(currentPeriodFilteredValues.total_net / 100);

  let vatTooltipParams: SpendParams | undefined;
  $: vatTooltipParams = $currentPeriodQuery.data
    ? {
        creditNoteVAT: currentPeriodFilteredValues.credit_note_vat,
        invoiceVAT: currentPeriodFilteredValues.invoice_vat,
        totalVAT: currentPeriodFilteredValues.total_vat,
        highlightedField: "totalVAT",
        accountNumber: null,
      }
    : undefined;

  let netTooltipParams: SpendParams | undefined;
  $: netTooltipParams = $currentPeriodQuery.data
    ? {
        invoiceNet: currentPeriodFilteredValues.net_invoice,
        creditNoteNet: currentPeriodFilteredValues.net_credit_note,
        totalNet: currentPeriodFilteredValues.total_net,
        highlightedField: "totalNet",
        accountNumber: null,
      }
    : undefined;
  let grossTooltipParams: SpendParams | undefined;
  $: grossTooltipParams = $currentPeriodQuery.data
    ? {
        invoiceGross:
          currentPeriodFilteredValues.net_invoice + currentPeriodFilteredValues.invoice_vat,
        creditNoteGross:
          currentPeriodFilteredValues.net_credit_note + currentPeriodFilteredValues.credit_note_vat,
        totalGross: currentPeriodFilteredValues.total_net + currentPeriodFilteredValues.total_vat,
        highlightedField: "totalGross",
        accountNumber: null,
      }
    : undefined;

  $: hasNoInvoices = !$currentPeriodQuery.isLoading && !$currentPeriodQuery.data?.invoice_count;

  $: subscriptionQuery = createGetSubscriptionQuery(buyerID);
  $: storeSupplier = $store.selectedSupplier;
  $: suppliers = $store.suppliers;

  const dateRangeFilters = [
    DateFilterType.Past7Days,
    DateFilterType.Past14Days,
    DateFilterType.ThisMonth,
  ];

  $: hasNoSubscriptionPlan =
    !!buyerID && $subscriptionQuery.data
      ? hasNoSubscription($subscriptionQuery.data.plan, $subscriptionQuery.data.status)
      : false;

  $: if ($querystring && buyerID && $subscriptionQuery.data) {
    maybeOpenSubscriptionModal($querystring, hasNoSubscriptionPlan);
  }

  $: isGPLoading = $gpQuery.isLoading || $gpQuery.isPlaceholderData;

  $: gpCaption =
    $gpQuery.data?.revenue_status === "complete" || isGPLoading
      ? `accurate for ${format($globalDatePickerStore.confirmedStartDate, GP_CAPTION_DATE_FORMAT, {
          locale: $dateLocale,
        })} - ${format($globalDatePickerStore.confirmedEndDate, GP_CAPTION_DATE_FORMAT, {
          locale: $dateLocale,
        })}`
      : $gpQuery.data?.revenue_status === "incomplete"
      ? "sales data may be incomplete for this period"
      : "";

  function maybeOpenSubscriptionModal(queryString: string, hasNoSubscriptionPlan: boolean) {
    const queryParams = new URLSearchParams(queryString);
    const subscription = queryParams.get("subscription");
    const upload = queryParams.get("upload");

    if (subscription === "premium") {
      track("signup_stripe_subscription_success_bkd");
      modal.onOpen();
    } else if (subscription === "new") {
      if (hasNoSubscriptionPlan) {
        subscriptionModal.open("invoice-upload");
      }
    } else if (upload === "true") {
      modal.onOpen();
    }
  }

  function sortBreakdownTotals(
    breakdowns: GetSpendReportOutput["chat_spend_breakdowns"],
    breakdownFilterType: SpendBreakdownFilterType
  ) {
    if (!breakdowns) {
      return [];
    }
    return breakdowns.sort((a, b) => {
      if (breakdownFilterType === "net") {
        return b.total_net - a.total_net;
      }
      return b.total_gross - a.total_gross;
    });
  }

  onMount(() => {
    restorePersistedChatFilters(chatFiltersByID);
    hasRestoredFilters = true;
    track("insights_landing_open", {
      supplier_id: storeSupplier?.id || "all",
      supplier_name: storeSupplier?.name || "all",
      buyer_id: $selectedBuyer?.id,
      buyer_name: $selectedBuyer?.name,
      pro_count: suppliers.filter((s) => s.buyer_id === $selectedBuyer?.id && s.pro).length,
    });
  });

  function hasAllChatsFiltersToggledOff(items: typeof chatFilterMenuItems) {
    return items && items.every((item) => !item.selected);
  }

  function clearModalParams() {
    const searchParams = new URLSearchParams(get(querystring));
    searchParams.delete("subscription");
    searchParams.delete("upload");
    replace(`${get(location)}?${searchParams.toString()}`);
  }

  function closeInvoiceUploadModal() {
    modal.onClose();
    clearModalParams();
  }
</script>

<main class="main">
  <section>
    <PageHeader title={t("overview.header.title")}>
      <svelte:fragment slot="left-aligned">
        <DatePickerPanel datePickerStore={globalDatePickerStore} {dateRangeFilters} />
      </svelte:fragment>
      <svelte:fragment slot="right-aligned">
        {#if !$currentPeriodQuery.isLoading && $currentPeriodQuery.data?.invoice_count}
          <ButtonNew
            size="xs"
            on:click={() => {
              if (!$subscriptionQuery.data) {
                return;
              }
              openSubscriptionModal(
                $subscriptionQuery.data.plan,
                $subscriptionQuery.data.status,
                modal.onOpen
              );
            }}
            bind:element={$triggerElement}
          >
            {t("overview.uploadInvoice.cta")}
          </ButtonNew>
        {/if}
      </svelte:fragment>
    </PageHeader>
    {#if !hasNoInvoices}
      <div class="overview">
        {#if $currentPeriodQuery.data && $previousPeriodQuery.data}
          <DisplayCard label={t("overview.totalNet.label")} tooltipParams={netTooltipParams}>
            <OverviewCardContent
              slot="display"
              value={filteredNetSpend}
              dateRangeType={$dateRangeFilterType}
              percentageChange={totalNetPercentChange}
            />
          </DisplayCard>
        {:else}
          <DisplayCard label={t("overview.totalNet.label")} />
        {/if}

        {#if $currentPeriodQuery.data && $previousPeriodQuery.data}
          <DisplayCard label={t("overview.totalGross.label")} tooltipParams={grossTooltipParams}>
            <OverviewCardContent
              slot="display"
              value={filteredGrossSpend}
              dateRangeType={$dateRangeFilterType}
              percentageChange={totalGrossPercentChange}
            />
          </DisplayCard>
        {:else}
          <DisplayCard label={t("overview.totalGross.label")} />
        {/if}

        {#if $currentPeriodQuery.data}
          <DisplayCard label={t("overview.tax.label")} tooltipParams={vatTooltipParams}>
            <OverviewCardContent slot="display" value={formattedVAT} />
          </DisplayCard>
        {:else}
          <DisplayCard label={t("overview.tax.label")} />
        {/if}
      </div>
    {/if}
  </section>
  {#if hasNoInvoices}
    <div class="empty-state">
      <UploadInvoiceEmptyState variant="overview" />
    </div>
  {:else}
    <section>
      <header class="breakdown-heading">
        <div>
          <TextNew as="h2" size="m" weight="medium">{t("overview.breakdown.title")}</TextNew>
        </div>

        <div class="breakdown-controls">
          <ButtonGroup>
            <RadioButton on:click={handleNetClick} isActive={breakdownFilterType === "net"}>
              {t("breakdown.net.title")}
            </RadioButton>
            <RadioButton on:click={handleGrossClick} isActive={breakdownFilterType === "gross"}>
              {t("breakdown.gross.title")}
            </RadioButton>
          </ButtonGroup>

          <ButtonNew
            {...breakdownFilterTriggerAttrs}
            on:click={breakdownFilterOnTriggerClick}
            bind:element={$breakdownFilterTriggerElement}
            variant="secondary"
            border
            size="xxs"
            textSize="xs"
          >
            <svelte:fragment>{t("global.filter.title")}</svelte:fragment>
            <svelte:fragment slot="rightAdornment">
              {#if breakdownFilterCount != null}
                <div class="gp-filter-count">
                  <div>
                    <TextNew weight="regular" size="xs" --rk-text-color="var(--rk-text-low)">
                      {`(${breakdownFilterCount})`}
                    </TextNew>
                  </div>
                </div>
              {/if}
            </svelte:fragment>
          </ButtonNew>
        </div>
      </header>
      <div class="chart-card">
        {#if breakdownFilterCount === 0}
          <div class="empty-state">
            <EmptyStateBasic
              title={""}
              subtitle={t("overview.emptyState.subtitle")}
              paddingBottom={false}
            >
              <svelte:component this={ListIcon} slot="illustration" />
            </EmptyStateBasic>
          </div>
        {:else}
          <BreakdownChart {breakdowns} {breakdownFilterType} />
        {/if}
      </div>
    </section>

    <section class="grid-col-2">
      <div class="overview-section price-change-widget">
        <div>
          <header class="heading">
            <IconedHeaderLink title={t("overview.topPriceChanges.title")} href="/prices">
              <Icon slot="icon" name="caretLeft" direction="s" --w="20px" --h="20px" />
            </IconedHeaderLink>
          </header>
          <TextNew size="xs" --rk-text-color="var(--rk-text-low)">
            {t("overview.topPriceChanges.subtitle")}
          </TextNew>
        </div>

        <TopPriceChangesCard
          xl={showAggregateGpWidget}
          priceChanges={filteredPriceChanges}
          isLoading={$topPriceChangesQuery.isLoading}
        />
      </div>

      <div class="overview-section gp-widget">
        <div>
          <header class="gp-heading">
            <IconedHeaderLink title={t("overview.gpTracker.title")} href="/gp">
              <Icon slot="icon" name="caretLeft" direction="s" --w="20px" --h="20px" />
            </IconedHeaderLink>
            <ButtonNew
              {...chatFilterTriggerAttrs}
              on:click={chatFilterOnTriggerClick}
              bind:element={$chatFilterTriggerElement}
              variant="secondary"
              border
              size="xxs"
              textSize="xs"
            >
              <svelte:fragment>{t("global.filter.title")}</svelte:fragment>
              <svelte:fragment slot="rightAdornment">
                {#if $chatFilterCount > 0}
                  <div class="gp-filter-count">
                    <div>
                      <TextNew weight="regular" size="xs" --rk-text-color="var(--rk-text-low)">
                        {`(${$chatFilterCount})`}
                      </TextNew>
                    </div>
                  </div>
                {/if}
              </svelte:fragment>
            </ButtonNew>
          </header>
          {#if $gpQuery?.data?.revenue_status === "complete" || $gpQuery?.data?.revenue_status === "incomplete" || isGPLoading}
            <TextNew size="xs" --rk-text-color="var(--rk-text-low)">
              {gpCaption}
            </TextNew>
          {:else}
            <button
              class="caption-btn"
              on:click={salesImportModal.onOpen}
              bind:this={$salesImportModalTriggerElement}
            >
              <TextNew size="xs" --rk-text-color="var(--rk-primary-accent)">
                {gpCaption}
                {t("overview.gpTracker.salesTitle")}
              </TextNew>
            </button>
          {/if}
        </div>

        <div>
          <GPCalculator
            {totalSpend}
            {totalRevenue}
            {grossProfit}
            {gpTarget}
            {isDefaultGp}
            {isProfitable}
            isLoading={isGPLoading}
            {onGpTargetChange}
          />
        </div>
      </div>

      {#if showAggregateGpWidget}
        <div class="overview-section aggregate-gp-widget">
          <div>
            <header class="gp-heading">
              <IconedHeaderLink
                title={"Aggregate " + t("overview.gpTracker.title")}
                href="/gp?view=aggregate"
              >
                <Icon slot="icon" name="caretLeft" direction="s" --w="20px" --h="20px" />
              </IconedHeaderLink>

              <div class="gp-filter-controls">
                <ButtonNew
                  {...buyerFilterTriggerAttrs}
                  on:click={buyerFilterOnTriggerClick}
                  bind:element={$buyerFilterTriggerElement}
                  variant="secondary"
                  border
                  size="xxs"
                  textSize="xs"
                >
                  <svelte:fragment>buyers</svelte:fragment>
                  <svelte:fragment slot="rightAdornment">
                    {#if $buyerFilterCount > 0}
                      <div class="gp-filter-count">
                        <div>
                          <TextNew weight="regular" size="xs" --rk-text-color="var(--rk-text-low)">
                            {`(${$buyerFilterCount})`}
                          </TextNew>
                        </div>
                      </div>
                    {/if}
                  </svelte:fragment>
                </ButtonNew>

                <ButtonNew
                  {...aggregateChatFilterTriggerAttrs}
                  on:click={aggregateChatFilterOnTriggerClick}
                  bind:element={$aggregateChatFilterTriggerElement}
                  variant="secondary"
                  border
                  size="xxs"
                  textSize="xs"
                >
                  <svelte:fragment>{t("global.filter.title")}</svelte:fragment>
                  <svelte:fragment slot="rightAdornment">
                    {#if $aggregateChatFilterCount > 0}
                      <div class="gp-filter-count">
                        <div>
                          <TextNew weight="regular" size="xs" --rk-text-color="var(--rk-text-low)">
                            {`(${$aggregateChatFilterCount})`}
                          </TextNew>
                        </div>
                      </div>
                    {/if}
                  </svelte:fragment>
                </ButtonNew>
              </div>
            </header>
          </div>

          <div>
            <GPCalculator
              readonly
              totalSpend={aggregateTotalSpend}
              totalRevenue={aggregateTotalRevenue}
              grossProfit={aggregateGrossProfit}
              gpTarget={aggregateGpTarget}
              isDefaultGp={aggregateIsDefaultGp}
              isProfitable={aggregateIsProfitable}
              isLoading={isAggregateGPLoading}
            />
          </div>
        </div>
      {/if}
    </section>
  {/if}
</main>

<InvoiceUploadModal
  contentAttrs={$contentAttrs}
  underlayAttrs={$underlayAttrs}
  open={$isUploadInvoiceOpen}
  contentAction={modal.content}
  onClose={closeInvoiceUploadModal}
/>

<SalesImportModal
  contentAction={salesImportModal.content}
  contentAttrs={$contentAttrs}
  underlayAttrs={$underlayAttrs}
  open={$open}
/>

<FilterMenu
  filterQueryParam={CHAT_FILTER_PARAM_KEY}
  isOpen={$chatFilterOpen}
  action={chatFilterMenu}
  menuAttrs={$chatFilterMenuAttrs}
  menuOptions={filterMenuOptions}
  items={chatFilterMenuItems}
  itemTitleSize="s"
  --menu-maxH="320px"
/>

<FilterMenu
  filterQueryParam="gp_buyer"
  onToggle={onToggleBuyer}
  onToggleAll={onToggleAllBuyers}
  isOpen={$buyerFilterOpen}
  action={buyerFilterMenu}
  menuAttrs={$buyerFilterMenuAttrs}
  menuOptions={filterMenuOptions}
  items={buyerFilterMenuItems}
  itemTitleSize="s"
  --menu-maxH="320px"
/>

<FilterMenu
  filterQueryParam="gp_chat"
  onToggle={onToggleAggregateChat}
  onToggleAll={onToggleAllAggregateChats}
  isOpen={$aggregateChatFilterOpen}
  action={aggregateChatFilterMenu}
  menuAttrs={$aggregateChatFilterMenuAttrs}
  menuOptions={filterMenuOptions}
  items={aggregateChatFilterMenuItems}
  itemTitleSize="s"
  --menu-maxH="320px"
/>

<BreakdownFilterMenu
  isOpen={$breakdownFilterOpen}
  action={breakdownFilterMenu}
  menuAttrs={$breakdownFilterMenuAttrs}
  menuOptions={breakdownFilterMenuOptions}
  items={$currentPeriodQuery.data?.chat_spend_breakdowns.map((breakdown) => ({
    id: breakdown.chat_id,
    title: breakdown.supplier_name,
    subtitle: breakdown.account_number,
  }))}
  bind:selected={breakdownChatFilters}
  --menu-maxH="320px"
/>

<style>
  .main {
    display: flex;
    flex-direction: column;
    gap: 40px;
    padding: 28px 24px;
    flex: 1 1 auto;
    height: 100%;
    overflow: scroll;
  }

  .empty-state {
    flex: 1 1 auto;
  }

  .overview-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .grid-col-2 {
    display: block;
    display: grid;
    grid-template-columns: repeat(2, minmax(min-content, 1fr));
    grid-template-rows: repeat(2, auto);
    align-items: start;
    gap: 24px;
  }

  .price-change-widget {
    grid-column: 1 / 2;
    grid-row: 1 / 3;
  }

  .gp-widget {
    grid-column: 2 / 3;
    grid-row: 1 / 2;
  }

  .aggregate-gp-widget {
    grid-column: 2 / 3;
    grid-row: 2 / 3;
  }

  .chart-card {
    border-radius: var(--rk-rounding-m);
    border: 1px solid var(--rk-divider-0);
    background-color: "#fff";
    margin-top: 8px;
    padding: 16px;
  }
  .overview {
    display: flex;
    flex: 1 1 100%;
    gap: 12px;
    margin-top: 20px;
  }

  .breakdown-heading {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
  }

  .breakdown-controls {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .heading {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    min-height: 32px;
  }

  .gp-heading {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .gp-filter-controls {
    display: flex;
    flex-direction: row;
    gap: 16px;
  }

  .caption-btn {
    display: block;
  }

  .gp-filter-count {
    margin-left: 4px;
  }
</style>
